import { useCallback } from 'react'

import { Button } from 'core-components/library/button'
import { NumberInput } from 'core-components/library/inputs'
import { IFlexItem, TWrapValues } from 'core-components/sitebuilder/models/flexGrid/IFlexItem'
import { FlexWrapperWrapSelector } from 'core-components/sitebuilder/settings/wrapper/flexWrapperHeaderSettings/FlexWrapperWrapSelector'

import styles from './FlexWrapperSettingsContent.module.scss'

export interface IFlexWrapperSettingsContent {
  item: IFlexItem
  updateItem: (newFlexItem: IFlexItem) => void
}

export const FlexWrapperSettingsContent = ({ item, updateItem }: IFlexWrapperSettingsContent) => {
  const breakpoints = item.wrapBreakpoints || []

  const addBreakpointHandler = useCallback(() => {
    const newItem: IFlexItem = {
      ...item,
      wrapBreakpoints: [...breakpoints, { minWidth: undefined, value: 'wrap' }],
    }
    updateItem(newItem)
  }, [breakpoints])

  const removeBreakpointHandler = useCallback(
    (index: number) => {
      const newWrapBreakpoints = [...breakpoints].filter((el, i) => index !== i)
      updateItem({ ...item, wrapBreakpoints: newWrapBreakpoints })
    },
    [breakpoints]
  )

  const maxWidthChangeHandler = useCallback(
    (newValue: string, index: number) => {
      const newWrapBreakpoints = [...breakpoints]
      newWrapBreakpoints[index].minWidth =
        newValue !== undefined ? parseInt(newValue, 10) : undefined
      updateItem({ ...item, wrapBreakpoints: newWrapBreakpoints })
    },
    [breakpoints]
  )

  const wrapValueChangeHandler = useCallback(
    (newValue: TWrapValues, index: number) => {
      const newWrapBreakpoints = [...breakpoints]
      newWrapBreakpoints[index].value = newValue
      updateItem({ ...item, wrapBreakpoints: newWrapBreakpoints })
    },
    [breakpoints]
  )

  return (
    <div className={styles.root}>
      <div className={styles.info}>
        You can manage the flex-wrap property for different screen widths.
        <br />
        <b>No wrap</b> value sets widget position in 1 row if there is free space for it
        <br />
        <b>Wrap</b> value sets widget offset for second row.
        <br />
      </div>
      {breakpoints.map((el, index) => {
        return (
          <div key={index} className={styles.item}>
            <NumberInput
              classes={{ root: styles.fullWidth }}
              label={'Min width'}
              value={el.minWidth}
              onChange={(newValue) => maxWidthChangeHandler(newValue, index)}
            />
            <FlexWrapperWrapSelector
              className={styles.fullWidth}
              value={el.value}
              onChange={(newValue) => wrapValueChangeHandler(newValue, index)}
            />
            <Button
              variant={'text'}
              color={'danger'}
              iconButton={'kbase kbase-delete'}
              onClick={() => removeBreakpointHandler(index)}
            />
          </div>
        )
      })}

      <Button variant={'text'} iconButton={'kbase kbase-add'} onClick={addBreakpointHandler} />
    </div>
  )
}
